/**
 * src/lib/useDictation.js — the ONE browser-side dictation hook.
 *
 * Card P193 (P183 PR 2). Extracted from AiPipelineReport.js, which P182 PR4
 * built inline, so that CRM intake and clinical notes reuse it rather than
 * carry copies. ⛔ The card's instruction is "don't write a second
 * implementation", and this repo has twice paid for ignoring that — a markdown
 * renderer that existed twice, a parser bug fixed in a helper and left alive in
 * an inline duplicate.
 *
 * ─── ⛔⛔ NO BROWSER SPEECH API ──────────────────────────────────────────────
 * This records audio with getUserMedia + MediaRecorder and sends it to OUR
 * backend, which runs AWS Transcribe Medical inside the BAA. It never touches
 * window.SpeechRecognition / webkitSpeechRecognition, which ship audio to
 * Google (Chrome) or Apple (Safari) — neither party to our BAA. #762 removed
 * those; tests/unit/security/noBrowserSpeechApi.test.js keeps them out.
 *
 * ⭐ THE TRANSCRIPT IS RETURNED, NEVER SUBMITTED. Every caller drops it into an
 * editable field and the user still has to act. A misheard word is cheap; a
 * misheard sentence acted on automatically is not.
 *
 * Usage:
 *   const d = useDictation({ send: api.dictateIntake });
 *   d.state        'idle' | 'recording' | 'transcribing'
 *   d.elapsed      seconds, while recording
 *   d.error        string | null
 *   d.start()      requests the mic and begins
 *   d.stop()       ends and transcribes
 *   d.available    false when the browser or the bridge cannot do it
 */

import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * ⚠ MUST MATCH MAX_CLIP_SECONDS in routes/dictation.js. The server refuses
 * anything longer, so a larger number here only produces a recording the user
 * waits for and then loses. Asserted in the unit suite.
 */
export const MAX_CLIP_SECONDS = 60;

export default function useDictation({ send, onTranscript } = {}) {
  const [state, setState] = useState('idle');
  const [error, setError] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const stopTimerRef = useRef(null);
  const elapsedRef = useRef(0);
  /*
   * ⚠ DECLARED HERE, WITH THE OTHER REFS, AND NOT BELOW `start`.
   * The first draft declared it after the callback that reads it. That works
   * — the closure resolves the binding when it RUNS, by which time the ref
   * exists — but it is the same temporal-dead-zone shape that threw at runtime
   * in P156 while `react-scripts build` reported "Compiled successfully".
   * Not worth being clever about.
   */
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;

  const available = typeof send === 'function'
    && typeof navigator !== 'undefined'
    && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
    && typeof window !== 'undefined'
    && typeof window.MediaRecorder !== 'undefined';

  const release = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.stream && typeof rec.stream.getTracks === 'function') {
      rec.stream.getTracks().forEach((t) => { try { t.stop(); } catch (_) { /* already stopped */ } });
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (stopTimerRef.current) { clearTimeout(stopTimerRef.current); stopTimerRef.current = null; }
  }, []);

  /*
   * ⛔ Release the microphone if the component unmounts mid-recording. A modal
   * that closes with the mic live leaves the browser's recording indicator on
   * and the stream open, and the user has no way to tell it stopped listening.
   */
  useEffect(() => release, [release]);

  const stop = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state !== 'inactive') { try { rec.stop(); } catch (_) { /* no-op */ } }
  }, []);

  const start = useCallback(async () => {
    setError(null);
    if (typeof send !== 'function') {
      setError('Dictation is unavailable in this build.'); return;
    }
    if (typeof navigator === 'undefined' || !navigator.mediaDevices
        || !navigator.mediaDevices.getUserMedia || typeof window.MediaRecorder === 'undefined') {
      setError('This browser cannot record audio. Type instead.'); return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (_) {
      setError('Microphone permission was refused.'); return;
    }
    let rec;
    try {
      rec = new window.MediaRecorder(stream);
    } catch (_) {
      setError('This browser cannot record audio. Type instead.');
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    recorderRef.current = rec;
    chunksRef.current = [];
    rec.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
    rec.onstop = async () => {
      release();
      setState('transcribing');
      try {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' });
        const buf = await blob.arrayBuffer();
        let binary = '';
        const bytes = new Uint8Array(buf);
        for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
        const r = await send({
          audioBase64: window.btoa(binary),
          mimeType: rec.mimeType || 'audio/webm',
          durationSeconds: Math.ceil(elapsedRef.current),
        });
        if (!r || r.error) {
          /*
           * ⚠ The SERVER'S message, not a generic one. An ACCESS_DENIED here
           * means a missing grant and a CLIP_TOO_LONG means a 61-second
           * recording; "something went wrong" sends someone hunting a bug.
           */
          setError((r && r.message) || 'The recording could not be transcribed.');
          if (typeof onTranscriptRef.current === 'function') onTranscriptRef.current(null);
        } else if (typeof onTranscriptRef.current === 'function') {
          onTranscriptRef.current(r.transcript || '');
        }
      } catch (err) {
        setError(err && err.message ? err.message : 'The recording could not be transcribed.');
      } finally {
        setState('idle');
        setElapsed(0);
      }
    };
    rec.start();
    setState('recording');
    setElapsed(0);
    elapsedRef.current = 0;
    timerRef.current = setInterval(() => {
      elapsedRef.current += 1;
      setElapsed(elapsedRef.current);
    }, 1000);
    /*
     * ⚠ HARD CAP, enforced by a timer rather than by the user remembering. A
     * microphone left on is both a bill and a recording nobody meant to make.
     */
    stopTimerRef.current = setTimeout(() => {
      try { if (rec.state !== 'inactive') rec.stop(); } catch (_) { /* no-op */ }
    }, MAX_CLIP_SECONDS * 1000);
  }, [send, release]);

  return { state, error, elapsed, start, stop, available, MAX_CLIP_SECONDS };
}
